import React, { useEffect, useState, useCallback, useRef } from 'react';
import Select from 'react-select';
// Remove unused Table import
import { fetchAuditList } from './api';
import AuditDetailDrawer from './AuditDetailDrawer';
import AuditRow from './AuditRow'; // Memoized
import TableSkeleton from './TableSkeleton';

const AuditList = ({ filters, onLoadingChange }) => {
    const [list, setList] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [isJumpingToEnd, setIsJumpingToEnd] = useState(false);
    // Cursor Pagination State
    const [pagination, setPagination] = useState({
        stack: [], // Stores START cursors of previous pages (null = page 1)
        nextCursor: null, // Cursor for the NEXT page
        pageSize: 10,
        hasMore: true
    });
    const [totalCount, setTotalCount] = useState(0);
    const [selectedId, setSelectedId] = useState(null);
    const [showDrawer, setShowDrawer] = useState(false);
    const [currentCursor, setCurrentCursor] = useState(null);
    const lastFetchParamsRef = useRef(null);

    // Notify parent about loading state
    useEffect(() => {
        if (onLoadingChange) {
            const isLoading = isInitialLoading || isTableLoading || isJumpingToEnd;
            onLoadingChange(isLoading);
        }
    }, [isInitialLoading, isTableLoading, isJumpingToEnd, onLoadingChange]);

    // Data Fetcher
    const loadData = useCallback(async (action, cursor, currentStack, isBackgroundRefresh = false) => {
        if (!isBackgroundRefresh) {
            if (action === 'first') setIsInitialLoading(true);
            else if (action === 'last') setIsJumpingToEnd(true);
            else setIsTableLoading(true);
        }

        try {
            const apiParams = {
                ...filters,
                limit: pagination.pageSize,
                action,
                encodedCursor: cursor
            };

            const res = await fetchAuditList(apiParams);

            if (res.success) {
                const newList = res.data.list || [];
                const resNextCursor = res.data.nextCursor;
                const resTotalCount = res.data.totalCount || 0;

                setList(newList);
                setTotalCount(resTotalCount);

                setPagination(prev => ({
                    ...prev,
                    nextCursor: resNextCursor,
                    hasMore: resNextCursor !== null,
                    stack: currentStack
                }));

                if (action === 'first') {
                    const cacheKey = `AI_INSIGHTS_LIST_CACHE_${JSON.stringify({ filters, pageSize: pagination.pageSize })}`;
                    sessionStorage.setItem(cacheKey, JSON.stringify({
                        list: newList,
                        totalCount: resTotalCount,
                        nextCursor: resNextCursor,
                        cacheTimestamp: Date.now()
                    }));
                }
            }
        } catch (e) {
            console.error("List Load Failed", e);
            if (action === 'first') {
                const cacheKey = `AI_INSIGHTS_LIST_CACHE_${JSON.stringify({ filters, pageSize: pagination.pageSize })}`;
                sessionStorage.removeItem(cacheKey);
            }
        } finally {
            setIsInitialLoading(false);
            setIsTableLoading(false);
            setIsJumpingToEnd(false);
        }
    }, [filters, pagination.pageSize]);

    // Initial Load & Filter Change (Resets)
    useEffect(() => {
        const fetchParams = JSON.stringify({ filters, pageSize: pagination.pageSize });
        const cacheKey = `AI_INSIGHTS_LIST_CACHE_${fetchParams}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        let cacheExists = false;

        if (cachedData) {
            const data = JSON.parse(cachedData);
            
            if (data === 'PENDING') {
                return;
            }

            if (data && typeof data === 'object') {
                const cacheTimestamp = data.cacheTimestamp || 0;
                const threeMinutes = 3 * 60 * 1000;
                const isExpired = Date.now() - cacheTimestamp > threeMinutes;

                if (!isExpired) {
                    setList(data.list || []);
                    setTotalCount(data.totalCount || 0);
                    setPagination(prev => ({
                        ...prev,
                        nextCursor: data.nextCursor,
                        hasMore: data.nextCursor !== null,
                        stack: []
                    }));
                    setIsInitialLoading(false);
                    lastFetchParamsRef.current = fetchParams;
                    return;
                } else {
                    setList(data.list || []);
                    setTotalCount(data.totalCount || 0);
                    setPagination(prev => ({
                        ...prev,
                        nextCursor: data.nextCursor,
                        hasMore: data.nextCursor !== null,
                        stack: []
                    }));
                    setIsInitialLoading(false);
                    cacheExists = true;
                }
            }
        }

        if (lastFetchParamsRef.current === fetchParams && !cacheExists) return;
        lastFetchParamsRef.current = fetchParams;

        setCurrentCursor(null);
        if (cacheExists) {
            sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));
            loadData('first', null, [], true);
        } else {
            sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));
            loadData('first', null, []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, pagination.pageSize, loadData]);

    const handleNext = () => {
        if (!pagination.nextCursor) return;
        // Push CURRENT cursor to stack
        const newStack = [...pagination.stack, currentCursor];
        // Limit stack size (Rule #2)
        if (newStack.length > 50) newStack.shift();

        setCurrentCursor(pagination.nextCursor);
        loadData('next', pagination.nextCursor, newStack);
    };

    const handlePrev = () => {
        if (pagination.stack.length === 0) return;

        const newStack = [...pagination.stack];
        const prevCursor = newStack.pop(); // Get last

        setCurrentCursor(prevCursor);
        loadData('next', prevCursor, newStack); // 'next' action is generic fetch-forward
    };

    const handleFirst = () => {
        setCurrentCursor(null);
        loadData('first', null, []);
    };

    const handleLast = () => {
        setCurrentCursor('LAST_PAGE_MARKER');
        loadData('last', null, []); // Stack cleared on Last
    };

    const handlePageSizeChange = (selectedOption) => {
        setPagination(prev => ({ ...prev, pageSize: selectedOption.value }));
    };

    // Removed redundant pageSize effect as it's now handled in the main effect with filter reset logic

    const pageSizeOptions = [
        { value: 10, label: '10' },
        { value: 25, label: '25' },
        { value: 50, label: '50' },
        { value: 100, label: '100' }
    ];

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '32px',
            height: '32px',
            fontSize: '12px',
            width: '120px',
            borderColor: '#e2e8f0',
            boxShadow: 'none',
            '&:hover': { borderColor: '#cbd5e1' }
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 8px',
            height: '30px',
        }),
        input: (provided) => ({
            ...provided,
            margin: '0',
            padding: '0'
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '30px'
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: '4px'
        }),
        menu: (provided) => ({
            ...provided,
            fontSize: '12px',
            zIndex: 50
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#f1f5f9' : null,
            color: state.isSelected ? 'white' : '#0f172a',
            cursor: 'pointer'
        })
    };

    return (
        <div className="aii-wrapper">
            <div className="aii-card">
                {/* ... Header ... */}

                <div className="aii-card-body p-0">
                    <div className="aii-table-container" style={{ minHeight: '300px' }}>
                        <table className="aii-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>User</th>
                                    <th>Prompt</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Loading State - Show Skeleton ONLY */}
                                {(isInitialLoading || isTableLoading || isJumpingToEnd) ? (
                                    <TableSkeleton rows={pagination.pageSize} />
                                ) : (
                                    /* Data State */
                                    <>
                                        {list.map(item => (
                                            <AuditRow key={item._id} item={item} onClick={(id) => { setSelectedId(id); setShowDrawer(true); }} />
                                        ))}

                                        {list.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center p-5">No records found</td>
                                            </tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION CONTROLS */}
                    <div className="aii-pagination-bar">
                        <div className="aii-pg-left">
                            <span className="text-muted text-small" style={{ fontSize: '13px', fontWeight: '500' }}>
                                {list.length > 0 ? (
                                    <>
                                        Showing <strong>{(pagination.stack.length * pagination.pageSize) + 1}</strong> - <strong>{(pagination.stack.length * pagination.pageSize) + list.length}</strong> of <strong>{totalCount}</strong>
                                    </>
                                ) : (
                                    'No records found'
                                )}
                            </span>
                        </div>
                        <div className="aii-pg-right">
                            <div style={{ marginRight: '16px' }}>
                                <Select
                                    value={pageSizeOptions.find(opt => opt.value === pagination.pageSize)}
                                    onChange={handlePageSizeChange}
                                    options={pageSizeOptions}
                                    styles={customSelectStyles}
                                    isSearchable={false}
                                    menuPlacement="top"
                                />
                            </div>

                            <button className="aii-btn-icon" onClick={handleFirst} disabled={isTableLoading || (!currentCursor && !isJumpingToEnd)}>
                                <i className="fa fa-angle-double-left"></i>
                            </button>
                            <button className="aii-btn-icon" onClick={handlePrev} disabled={isTableLoading || pagination.stack.length === 0}>
                                <i className="fa fa-angle-left"></i>
                            </button>
                            <button className="aii-btn-icon" onClick={handleNext} disabled={isTableLoading || !pagination.nextCursor || isJumpingToEnd}>
                                <i className="fa fa-angle-right"></i>
                            </button>
                            <button className="aii-btn-icon" onClick={handleLast} disabled={isTableLoading || isJumpingToEnd || (!pagination.nextCursor && !isJumpingToEnd)}>
                                <i className="fa fa-angle-double-right"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <AuditDetailDrawer show={showDrawer} onHide={() => setShowDrawer(false)} recordId={selectedId} />
            </div>
        </div>
    );
};


export default AuditList;

